import styled, { css } from "styled-components";
import { Box, Flex } from "rebass/styled-components";

export type TDockTone = "neutral" | "ready" | "caution" | "alert";

const toneColor = (tone: TDockTone, colors: Record<string, string>) =>
  ({
    neutral: colors.chalk,
    ready: colors.envy,
    caution: colors.caution,
    alert: colors.alert,
  })[tone];

export const focusRing = css`
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }
`;

export const DockRoot = styled.section`
  align-items: flex-end;
  bottom: 0;
  display: flex;
  flex-direction: column;
  padding: 0 20px 20px 0;
  pointer-events: none;
  position: fixed;
  right: 0;
  z-index: 101;

  > * {
    pointer-events: auto;
  }

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    align-items: stretch;
    left: 0;
    padding: 0 12px 12px;
  }

  @media (max-width: 480px) {
    padding-bottom: 100px;
  }
`;

// Shared by every card in the corner, including the clip tracker cards.
export const DockSurface = styled(Box)`
  background-color: ${({ theme }) => theme.colors.navy300};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  color: ${({ theme }) => theme.colors.ice};
  padding: 10px 15px;
  width: 370px;

  @media (max-width: ${({ theme }) => theme.breaks.small}) {
    width: 100%;
  }
`;

export const DockCloseButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 32px;
  justify-content: center;
  margin: -6px -10px -6px 0;
  padding: 0;
  transition:
    color 0.15s ease-out,
    background-color 0.15s ease-out;
  width: 32px;

  &:hover {
    background-color: ${({ theme }) => theme.colors.chalkAlpha10};
    color: ${({ theme }) => theme.colors.ice};
  }

  ${focusRing}
`;

export const CardRow = styled(Flex)`
  align-items: center;
  gap: 10px;
`;

export const IconSlot = styled(Flex)<{ $tone: TDockTone }>`
  align-items: center;
  color: ${({ $tone, theme }) => toneColor($tone, theme.colors)};
  flex-shrink: 0;
  height: 20px;
  justify-content: center;
  width: 20px;

  img {
    height: 20px;
    width: 20px;
  }
`;

export const CardTitle = styled(Box).attrs({ variant: "text.labelMedium" })`
  color: ${({ theme }) => theme.colors.ice};
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const MetaRow = styled(Flex)`
  align-items: center;
  gap: 10px;
  margin-top: 6px;
  padding-left: 30px;
`;

export const CardMeta = styled(Box).attrs({ variant: "text.labelSmall" })`
  color: ${({ theme }) => theme.colors.chalk};
  flex: 1;
  min-width: 0;
`;

export const ActionButton = styled.button`
  background-color: ${({ theme }) => theme.colors.chalkAlpha10};
  border: none;
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  flex-shrink: 0;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  padding: 5px 12px;
  transition: background-color 0.15s ease-out;

  &:hover {
    background-color: ${({ theme }) => theme.colors.chalkAlpha20};
  }

  ${focusRing}
`;

export const MoreButton = styled.button`
  background-color: ${({ theme }) => theme.colors.navy300};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 999px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  padding: 5px 12px;
  transition: color 0.15s ease-out;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }

  ${focusRing}
`;

export const SteambotModalBody = styled(Flex)`
  background: ${({ theme }) => theme.colors.ned};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 12px;
  color: ${({ theme }) => theme.colors.ice};
  flex-direction: column;
  gap: 16px;
  max-height: 90vh;
  max-width: 480px;
  overflow-y: auto;
  padding: 28px;
  position: relative;
  width: 90vw;
`;

export const SteambotModalTitle = styled(Box)`
  color: ${({ theme }) => theme.colors.ice};
  font-family: "Barlow", sans-serif;
  font-size: 20px;
  font-weight: 700;
`;

export const VisuallyHidden = styled.div`
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

export const SurveySurface = styled(DockSurface)`
  padding: 16px;
`;

export const SurveyHeader = styled(Flex)`
  align-items: flex-start;
  gap: 12px;
`;

export const SurveyIcon = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.envy};
  flex-shrink: 0;
  justify-content: center;
  padding-top: 2px;
`;

export const SurveyTitle = styled.div`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 17px;
  font-weight: 700;
  line-height: 1.2;
`;

export const SurveyBody = styled.div`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 13.5px;
  line-height: 1.45;
  margin-top: 5px;
`;

export const SurveyPrimaryButton = styled.button`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.envy};
  border: none;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.navy600};
  cursor: pointer;
  display: flex;
  font-family: inherit;
  font-size: 15px;
  font-weight: 700;
  gap: 8px;
  justify-content: center;
  margin-top: 14px;
  padding: 11px 16px;
  transition: filter 0.15s ease-out;
  width: 100%;

  &:hover {
    filter: brightness(1.08);
  }

  ${focusRing}
`;

export const SurveySecondaryRow = styled(Flex)`
  gap: 10px;
  margin-top: 10px;
`;

export const SurveySecondaryButton = styled.button`
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  flex: 1;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  padding: 9px 12px;
  transition: background-color 0.15s ease-out;

  &:hover {
    background-color: ${({ theme }) => theme.colors.chalkAlpha10};
  }

  ${focusRing}
`;
